import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import courseService from '../services/course.service';
import { slugifyWithFallback } from '../utils/slug';

const COURSE_PLACEHOLDER = '/images/course-placeholder.svg';

const formatClassLevel = (level) => {
  if (!level) {
    return null;
  }
  const trimmed = `${level}`.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase().startsWith('class') ? trimmed : `Class ${trimmed}`;
};

const buildExamLabel = (exam) => {
  if (!exam) {
    return null;
  }
  const name = exam.name || 'Exam';
  return exam.year ? `${name} ${exam.year}` : name;
};

const buildCourseCard = (course) => ({
  id: course.id,
  name: course.name || 'Untitled Course',
  cover: course.cover_image_url || COURSE_PLACEHOLDER,
  classLabel: formatClassLevel(course.class_level),
  examLabels: (course.exam_details || []).map(buildExamLabel).filter(Boolean),
  slug: slugifyWithFallback(course.name, `course-${course.id}`),
});

const HomePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || loading) {
      return;
    }

    let isMounted = true;

    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        setError('');
        const response = await courseService.getCourses({ include_sections: 1, include_subject_groups: 1 });
        if (!isMounted) {
          return;
        }
        const normalized = Array.isArray(response)
          ? response
              .filter((course) => {
                const subjectGroups = Array.isArray(course.subject_groups) ? course.subject_groups : [];
                return subjectGroups.some((group) => {
                  const sections = Array.isArray(group.sections) ? group.sections : [];
                  return sections.some((section) => Array.isArray(section.units) && section.units.length > 0);
                });
              })
              .map(buildCourseCard)
          : [];
        setCourses(normalized);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load courses', err);
        setError('Unable to load courses right now. Please try again later.');
        setCourses([]);
      } finally {
        if (isMounted) {
          setLoadingCourses(false);
        }
      }
    };

    fetchCourses();

    return () => {
      isMounted = false;
    };
  }, [user, loading]);

  return (
    <div className="min-h-screen bg-bg-default">
      <div className="page-container py-8">
        <div className="flex flex-col gap-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-primary">Discover Courses</h1>
            <p className="text-text-secondary">Choose a course to explore the syllabus and start learning.</p>
          </div>

          {error && (
            <div className="rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
              {error}
            </div>
          )}

          <section>
            <h2 className="text-xl font-semibold text-primary mb-4">Available Courses</h2>

            {loadingCourses ? (
              <div className="py-16 text-center text-text-secondary">Loading courses...</div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {courses.length === 0 ? (
                  <div className="col-span-full text-text-secondary">No courses found.</div>
                ) : (
                  courses.map((course) => (
                    <button
                      type="button"
                      key={course.id}
                      onClick={() => navigate(`/courses/${course.slug}`, { state: { courseId: course.id } })}
                      className="card overflow-hidden text-left transition-shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <div className="aspect-video bg-muted-gray/10 overflow-hidden">
                        <img
                          src={course.cover || COURSE_PLACEHOLDER}
                          alt={course.name}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.src = COURSE_PLACEHOLDER;
                          }}
                        />
                      </div>
                      <div className="p-4 space-y-2">
                        <h3 className="text-lg font-semibold text-primary leading-snug">
                          {course.name}
                        </h3>
                        {(course.classLabel || course.examLabels.length > 0) && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {course.classLabel && (
                              <span className="inline-flex items-center rounded-full bg-brand-yellow/90 px-3 py-1 text-xs font-semibold text-black shadow-sm shadow-black/10">
                                {course.classLabel}
                              </span>
                            )}
                            {course.examLabels.map((label) => (
                              <span
                                key={`${course.id}-${label}`}
                                className="inline-flex items-center rounded-full bg-brand-yellow/90 px-3 py-1 text-xs font-semibold text-black shadow-sm shadow-black/10"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
